import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/Table';
import { Text } from '@/ui';
import { ApiKey } from '@/services/ApiKeys/org-api-key.service';
import { maskApiKey, copyToClipboard } from '@/utils/helpers/apiKeyHelpers';
import { formatDateToNow } from '@/utils/helpers/dateFormater';
import ApiKeysActions from './ApiKeysActions';

type Props = {
  apiKeys: ApiKey[];
};

function ApiKeysTable({ apiKeys }: Props) {
  const { t } = useTranslation();

  const handleCopyKey = async (publicKey: string) => {
    try {
      await copyToClipboard(publicKey);
      toast.success(t('ApiKeys.copy_success'));
    } catch {
      toast.error(t('ApiKeys.copy_error'));
    }
  };

  const permissionLabels = useMemo(
    () =>
      apiKeys.map((apiKey) => {
        const active = Object.entries(apiKey.permissions)
          .filter(([, value]) => value)
          .map(([key]) => t(`ApiKeys.permission_${key}`));
        return active.length > 0 ? active.join(', ') : '-';
      }),
    [apiKeys, t],
  );

  return (
    <div className='w-full rounded-md border'>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t('ApiKeys.column_name')}</TableHead>
            <TableHead>{t('ApiKeys.column_permissions')}</TableHead>
            <TableHead>{t('ApiKeys.column_created')}</TableHead>
            <TableHead>{t('ApiKeys.column_created_by')}</TableHead>
            <TableHead>{t('ApiKeys.column_public_key')}</TableHead>
            <TableHead className='w-[50px]'></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {apiKeys.map((apiKey, i) => (
            <TableRow key={apiKey._id}>
              <TableCell>
                <Text size='s'>{apiKey.name || '-'}</Text>
              </TableCell>
              <TableCell>
                <Text size='xs' color='base-grey'>
                  {permissionLabels[i]}
                </Text>
              </TableCell>
              <TableCell>
                <Text size='xs' color='base-grey'>
                  {apiKey.createdAt ? formatDateToNow(apiKey.createdAt) : '-'}
                </Text>
              </TableCell>
              <TableCell>
                <Text size='xs' color='base-grey'>
                  {apiKey.createdBy && apiKey.users?.[0]
                    ? `${apiKey.users[0].firstName} ${apiKey.users[0].lastName}`
                    : '-'}
                </Text>
              </TableCell>
              <TableCell>
                <button
                  onClick={() => handleCopyKey(apiKey.publicApiKey)}
                  className='text-left hover:text-primary transition-colors cursor-pointer'
                  title={t('ApiKeys.click_to_copy')}
                >
                  <Text size='xs' color='blue'>
                    {maskApiKey(apiKey.publicApiKey)}
                  </Text>
                </button>
              </TableCell>
              <TableCell>
                <ApiKeysActions apiKeyId={apiKey._id} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export default ApiKeysTable;
